import React, { useState } from 'react';
import { Search, Eye, CheckSquare, Square } from 'lucide-react';
import PropertyDetailsModal from './PropertyDetailsModal';

const ComparablesTable = ({
  comparableData = [],
  currentAddress = '',
  selectedComparables = [],
  onComparableSelection,
  // NEW: Props for AI analysis selection
  selectedForAI = [],
  onAISelectionChange,
  isAIAnalyzing = false,
  // NEW: Props for bracketing display (OPTIONAL)
  bracketingData = null
}) => {
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // ✅ FIX ISSUE #8: Add state for reasoning modal
  const [selectedReasoning, setSelectedReasoning] = useState(null);
  const [isReasoningModalOpen, setIsReasoningModalOpen] = useState(false);

  const handleViewDetails = (property) => {
    setSelectedProperty(property);
    setIsModalOpen(true);
  };

  // ✅ FIX ISSUE #8: Handler for opening reasoning modal
  const handleViewReasoning = (comp) => {
    setSelectedReasoning({
      address: comp.address,
      aiScore: comp.aiScore,
      photoSimilarityScore: comp.photoSimilarityScore,
      dataBracketingScore: comp.dataBracketingScore,
      reasoning: comp.aiReasoning,
      hasPhotos: comp.hasPhotos
    });
    setIsReasoningModalOpen(true);
  };

  // Check if a comparable is selected for side-by-side comparison
  const isComparableSelected = (comparable) => {
    return selectedComparables.some(selected =>
      (selected.mlsNumber || selected.address) === (comparable.mlsNumber || comparable.address)
    );
  };

  // Check if a comparable is selected for AI analysis
  const isSelectedForAI = (comparable, index) => {
    if (!selectedForAI || selectedForAI.length === 0) return false;
    return selectedForAI.some(sel => sel.index === index);
  };

  // Handle checkbox change for comparison
  const handleCheckboxChange = (comparable, isChecked) => {
    if (onComparableSelection) {
      onComparableSelection(comparable, isChecked);
    }
  };

  // Handle checkbox change for AI analysis
  const handleAICheckboxChange = (comparable, index, isChecked) => {
    if (onAISelectionChange) {
      onAISelectionChange(comparable, index, isChecked);
    }
  };

  // Handle Select All for AI analysis
  const handleSelectAllAI = (isChecked) => {
    if (onAISelectionChange) {
      comparableData.forEach((comp, index) => {
        onAISelectionChange(comp, index, isChecked);
      });
    }
  };

  // Check if all are selected for AI
  const allSelectedForAI = selectedForAI && selectedForAI.length === comparableData.length;
  const someSelectedForAI = selectedForAI && selectedForAI.length > 0 && selectedForAI.length < comparableData.length;

  // NEW: Get bracket status for a comparable (OPTIONAL)
  const getBracketStatus = (comp, index) => {
    if (!bracketingData || !bracketingData.comparablesWithBrackets) return null;
    return bracketingData.comparablesWithBrackets[index]?.brackets;
  };

  // NEW: Render bracket badge (OPTIONAL)
  const renderBracketBadge = (status) => {
    if (!status) return null;
    if (status === 'above') {
      return <span className="text-blue-400 text-xs ml-1" title="Above subject">⬆️</span>;
    } else if (status === 'below') {
      return <span className="text-purple-400 text-xs ml-1" title="Below subject">⬇️</span>;
    } else if (status === 'equal') {
      return <span className="text-green-400 text-xs ml-1" title="Equal to subject">✓</span>;
    }
    return null;
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProperty(null);
  };

  if (comparableData.length === 0) {
    return (
      <div className="text-center py-12">
        <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400 text-lg mb-2">No Comparable Properties Found</p>
        <p className="text-gray-500 text-sm">
          {currentAddress ? 'Try expanding the search radius or check the address.' : 'Enter a property address to search for comparable sales.'}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                {/* AI Analysis Selection Column with Select All */}
                <th className="text-left p-3 text-gray-300">
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-xs text-purple-400">AI Analyze</span>
                    <button
                      onClick={() => handleSelectAllAI(!allSelectedForAI)}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors"
                      title={allSelectedForAI ? "Deselect all for AI analysis" : "Select all for AI analysis"}
                      disabled={isAIAnalyzing}
                    >
                      {allSelectedForAI ? (
                        <CheckSquare className="w-4 h-4 text-purple-500" />
                      ) : someSelectedForAI ? (
                        <div className="w-4 h-4 border-2 border-purple-500 rounded flex items-center justify-center">
                          <div className="w-2 h-2 bg-purple-500 rounded-sm"></div>
                        </div>
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                      <span className="text-[10px]">All</span>
                    </button>
                  </div>
                </th>
                <th className="text-left p-3 text-gray-300">
                  <div className="flex items-center gap-2">
                    <span className="text-xs">Compare</span>
                  </div>
                </th>
                <th className="text-left p-3 text-gray-300">Actions</th>
                <th className="text-left p-3 text-gray-300">Address</th>
                <th className="text-left p-3 text-gray-300">Sale Date</th>
                <th className="text-left p-3 text-gray-300">Distance (mi)</th>
                <th className="text-left p-3 text-gray-300">Beds</th>
                <th className="text-left p-3 text-gray-300">Baths</th>
                <th className="text-left p-3 text-gray-300">Living Area (SqFt)</th>
                <th className="text-left p-3 text-gray-300">Sale Price</th>
                <th className="text-left p-3 text-gray-300">Lot Size (SqFt)</th>
                <th className="text-left p-3 text-gray-300">MLS Status</th>
                <th className="text-left p-3 text-gray-300">Similarity Score</th>
                <th className="text-left p-3 text-gray-300 min-w-[200px]">🤖 AI Score & Reasoning</th>
              </tr>
            </thead>
            <tbody>
              {comparableData.map((comp, index) => (
                <tr
                  key={`comparable-${index}-${comp.mlsNumber || ''}-${comp.address || ''}-${comp.price || ''}`}
                  className={`border-t border-gray-700 hover:bg-gray-750 ${
                    isSelectedForAI(comp, index) ? 'bg-purple-900/20' : ''
                  } ${comp.aiScore ? 'bg-green-900/10' : ''}`}
                >
                  {/* AI Analysis Checkbox */}
                  <td className="p-3 text-center">
                    <input
                      type="checkbox"
                      checked={isSelectedForAI(comp, index)}
                      onChange={(e) => handleAICheckboxChange(comp, index, e.target.checked)}
                      className="w-4 h-4 accent-purple-600"
                      title="Select for AI photo analysis"
                      disabled={isAIAnalyzing}
                    />
                  </td>
                  {/* Comparison Checkbox */}
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={isComparableSelected(comp)}
                      onChange={(e) => handleCheckboxChange(comp, e.target.checked)}
                      className="w-4 h-4"
                      title="Select for side-by-side comparison"
                    />
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => handleViewDetails(comp.rawData || comp)}
                      className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                      title="View full property details"
                    >
                      <Eye className="w-3 h-3" />
                      Details
                    </button>
                  </td>
                  <td className="p-3 text-sm">
                    <div className="min-w-[200px] max-w-[300px]" title={comp.address}>
                      {comp.address || 'N/A'}
                    </div>
                  </td>
                  <td className="p-3">{comp.date}</td>
                  <td className="p-3">{comp.distance}</td>
                  <td className="p-3">
                    {comp.beds}
                    {bracketingData && renderBracketBadge(getBracketStatus(comp, index)?.bedrooms)}
                  </td>
                  <td className="p-3">
                    {comp.baths}
                    {bracketingData && renderBracketBadge(getBracketStatus(comp, index)?.bathrooms)}
                  </td>
                  <td className="p-3">
                    {comp.livingArea ? comp.livingArea.toLocaleString() : '-'}
                    {bracketingData && renderBracketBadge(getBracketStatus(comp, index)?.gla)}
                  </td>
                  <td className="p-3 font-semibold text-green-400">{comp.salePrice}</td>
                  <td className="p-3">
                    {comp.lotSize ? parseInt(comp.lotSize).toLocaleString() : comp.rawData?.LotSizeSquareFeet ? parseInt(comp.rawData.LotSizeSquareFeet).toLocaleString() : '-'}
                    {bracketingData && renderBracketBadge(getBracketStatus(comp, index)?.lotSize)}
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 text-xs rounded font-medium ${
                      (comp.status || '').toLowerCase().includes('active') ? 'bg-green-600 text-white' :
                      (comp.status || '').toLowerCase().includes('pending') ? 'bg-yellow-600 text-white' :
                      (comp.status || '').toLowerCase().includes('sold') ? 'bg-blue-600 text-white' :
                      (comp.status || '').toLowerCase().includes('expired') ? 'bg-gray-600 text-white' :
                      (comp.status || '').toLowerCase().includes('cancel') ? 'bg-red-600 text-white' :
                      'bg-gray-700 text-gray-300'
                    }`}>
                      {comp.status || comp.rawData?.StandardStatus || comp.rawData?.MlsStatus || 'N/A'}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 text-white text-xs rounded ${
                      comp.similarityScore >= 85 ? 'bg-green-600' :
                      comp.similarityScore >= 75 ? 'bg-blue-600' :
                      comp.similarityScore >= 65 ? 'bg-yellow-600' : 'bg-gray-600'
                    }`}>
                      {comp.similarityScore}%
                    </span>
                  </td>
                  {/* Enhanced AI Score Column with Reasoning */}
                  <td className="p-3">
                    {comp.aiScore ? (
                      <div className="flex flex-col gap-2">
                        {/* Score badges */}
                        <div className="flex flex-wrap gap-1">
                          <span className={`px-2 py-1 text-white text-xs rounded font-bold ${
                            comp.aiScore >= 90 ? 'bg-purple-600' :
                            comp.aiScore >= 80 ? 'bg-blue-600' :
                            comp.aiScore >= 70 ? 'bg-green-600' :
                            comp.aiScore >= 60 ? 'bg-yellow-600' : 'bg-gray-600'
                          }`}>
                            🤖 {comp.aiScore}%
                          </span>
                          {comp.photoSimilarityScore !== undefined && (
                            <span className="px-1.5 py-0.5 bg-blue-800 text-blue-200 text-[10px] rounded" title="Photo Similarity">
                              📷 {comp.photoSimilarityScore}%
                            </span>
                          )}
                          {comp.dataBracketingScore !== undefined && (
                            <span className="px-1.5 py-0.5 bg-cyan-800 text-cyan-200 text-[10px] rounded" title="Data Bracketing">
                              📊 {comp.dataBracketingScore}%
                            </span>
                          )}
                        </div>
                        {/* ✅ FIX ISSUE #8: Enhanced reasoning text with See More button */}
                        {comp.aiReasoning && (
                          <div className="text-xs text-gray-300 bg-gray-700/50 p-2 rounded max-w-xs">
                            <p className="line-clamp-3">
                              {comp.aiReasoning}
                            </p>
                            <button
                              onClick={() => handleViewReasoning(comp)}
                              className="mt-1 text-blue-400 hover:text-blue-300 underline text-[11px]"
                            >
                              See more →
                            </button>
                          </div>
                        )}
                        {/* No photos warning */}
                        {comp.hasPhotos === false && (
                          <span className="text-[10px] text-orange-400">⚠️ No photos (data-only)</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-500">
                        {isSelectedForAI(comp, index) ? '⏳ Pending...' : '-'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Selection Summary */}
      {selectedForAI && selectedForAI.length > 0 && (
        <div className="mt-2 text-sm text-purple-400">
          {selectedForAI.length} comparable{selectedForAI.length !== 1 ? 's' : ''} selected for AI analysis
        </div>
      )}

      {/* Property Details Modal */}
      <PropertyDetailsModal
        property={selectedProperty}
        isOpen={isModalOpen}
        onClose={closeModal}
      />

      {/* ✅ FIX ISSUE #8: AI Reasoning Modal */}
      {isReasoningModalOpen && selectedReasoning && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-purple-900 to-blue-900 p-4 border-b border-gray-700">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-1">🤖 AI Analysis Details</h3>
                  <p className="text-sm text-gray-300">{selectedReasoning.address}</p>
                </div>
                <button
                  onClick={() => setIsReasoningModalOpen(false)}
                  className="text-gray-400 hover:text-white text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Score Breakdown */}
              <div className="mb-6 bg-gray-700/50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-white mb-3">Score Breakdown</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-300">Overall AI Score:</span>
                    <span className={`px-3 py-1 text-white text-sm rounded font-bold ${
                      selectedReasoning.aiScore >= 90 ? 'bg-purple-600' :
                      selectedReasoning.aiScore >= 80 ? 'bg-blue-600' :
                      selectedReasoning.aiScore >= 70 ? 'bg-green-600' :
                      selectedReasoning.aiScore >= 60 ? 'bg-yellow-600' : 'bg-gray-600'
                    }`}>
                      {selectedReasoning.aiScore}%
                    </span>
                  </div>
                  {selectedReasoning.photoSimilarityScore !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-300">📷 Photo Similarity:</span>
                      <span className="px-3 py-1 bg-blue-700 text-white text-sm rounded font-semibold">
                        {selectedReasoning.photoSimilarityScore}%
                      </span>
                    </div>
                  )}
                  {selectedReasoning.dataBracketingScore !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-300">📊 Data Bracketing:</span>
                      <span className="px-3 py-1 bg-cyan-700 text-white text-sm rounded font-semibold">
                        {selectedReasoning.dataBracketingScore}%
                      </span>
                    </div>
                  )}
                  {selectedReasoning.hasPhotos === false && (
                    <div className="mt-2 p-2 bg-orange-900/30 border border-orange-700 rounded">
                      <p className="text-xs text-orange-300">⚠️ Data-only analysis (no photos available)</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Detailed Reasoning */}
              <div className="bg-gray-700/30 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-white mb-3">Detailed Analysis</h4>
                <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">
                  {selectedReasoning.reasoning}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-800 border-t border-gray-700 p-4">
              <button
                onClick={() => setIsReasoningModalOpen(false)}
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ComparablesTable;
